import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { ArrowRight, Check } from 'lucide-react'
import { Reveal } from '@/components/generals/reveal'
import { Eyebrow } from '@/components/generals/eyebrow'
import { ButtonLink } from '@/components/generals/button'
import { lifestyleBenefits } from '@/constants/estate'

/*
   Right-sized living
   A deep green band written for down-scalers: a portrait photograph on the
   left and the benefits list with a viewing call to action on the right.
*/
export const LifestyleSection: FC = (): ReactNode => {
    return (
        <section id='lifestyle' aria-labelledby='lifestyle-heading' className='overflow-hidden bg-forest text-cream'>
            <div className='mx-auto grid max-w-7xl items-center gap-14 px-6 py-24 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:py-32'>
                <Reveal className='lg:col-span-5'>
                    <div className='relative aspect-[4/5] overflow-hidden rounded-3xl bg-forest-deep'>
                        <Image
                            src='/images/lifestyle-patio.jpg'
                            alt='A couple sharing morning coffee on the covered patio of a single-storey home'
                            fill
                            sizes='(min-width: 1024px) 40vw, 100vw'
                            className='object-cover'
                        />
                    </div>
                </Reveal>

                <Reveal delay={120} className='lg:col-span-6 lg:col-start-7'>
                    <Eyebrow tone='light'>
                        Right-sized living
                    </Eyebrow>

                    <h2 id='lifestyle-heading' className='mt-6 font-heading text-4xl leading-[1.08] sm:text-5xl'>
                        Less house to manage.
                        <br />
                        <em className='italic text-cream/75'>More life to enjoy.</em>
                    </h2>

                    <p className='mt-6 text-[1.0625rem] leading-relaxed text-cream/75'>
                        For many buyers, Rockwood is the next chapter after the big family home. Everything you loved about a house, a garden and a fire on a winter's night, without the stairs, the upkeep or the worry.
                    </p>

                    <ul className='mt-10 divide-y divide-cream/10 border-y border-cream/10'>
                        {lifestyleBenefits.map(benefit => (
                            <li key={benefit} className='flex items-center gap-4 py-4 text-[0.9375rem]'>
                                <span className='inline-flex size-7 shrink-0 items-center justify-center rounded-full border border-cream/25'>
                                    <Check aria-hidden='true' className='size-3.5' />
                                </span>

                                {benefit}
                            </li>
                        ))}
                    </ul>

                    <ButtonLink href='#enquire' variant='light' size='lg' className='mt-10'>
                        Arrange a private viewing
                        <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover/button:translate-x-0.5' />
                    </ButtonLink>
                </Reveal>
            </div>
        </section>
    )
}
